{
  "stepOf": "Step {{step}} of {{total}}",
  "actions": { "continue": "Continue", "retry": "Try again" },
  "welcome": {
    "title": "Calmer days start with a plan",
    "lead": "Nap times that fit your baby, a bedtime routine and gentle reminders. Two minutes to set up.",
    "start": "Get started",
    "signedInAs": "Signed in as {{email}}.",
    "notYou": "Not you?"
  },
  "baby": {
    "title": "Who are we planning for?",
    "lead": "Sleep needs change week by week, so we start with age.",
    "name": "Baby's name",
    "namePlaceholder": "First name or nickname",
    "birthDate": "Birth date",
    "early": "Was {{name}} born early?",
    "earlyNo": "No, on time",
    "earlyYes": "Yes, early",
    "dueDate": "Due date",
    "correctedHint": "We'll use corrected age (counted from the due date) for sleep advice, as pediatricians do.",
    "yourBaby": "your baby"
  },
  "consent": {
    "_legal": "DRAFT for lawyer review (docs/legal-review.md). Must match content/en/legal/privacy.md and health-data-policy.md. When the final text ships, bump HEALTH_CONSENT_VERSION on the server so the consent log records which text was agreed to.",
    "title": "Your baby's data, your choice",
    "titleAgain": "Please confirm again",
    "lead": "Baby Plan collects health information about your baby and you. We need your consent before we start.",
    "points": [
      "What we collect: what you and your family log (sleep, feeds, pumping, diapers, medicine and doses, growth, temperature, notes), your baby's birth and due dates, your quiz answers and your own mood check-ins.",
      "Why: to build your sleep plan, show trends, sync your family's phones and send reminders. We never sell it or use it for ads.",
      "Who sees it: you and the family members and caregivers you invite. Companies that run Baby Plan for us, like our hosting provider, process it only on our behalf.",
      "Sleep coach chat: when you use it, your question, your baby's first name and age, your quiz answers, and a summary of recent logs and check-ins go to our AI provider, Anthropic, to write the answer. Notes and medicine logs are never sent.",
      "Your choice: you can download your data, withdraw consent or delete your account at any time in Settings › Data and privacy. If you withdraw, we stop saving new logs."
    ],
    "rights": "Details: <privacy>Privacy Policy</privacy> and <health>Consumer Health Data Privacy Policy</health>. Questions: support@getbabyplan.com.",
    "checkbox": "I agree that Baby Plan may collect and use my baby's and my health information as described above.",
    "agree": "Agree and continue"
  },
  "quiz": {
    "loading": "Loading questions…",
    "progress": "Question {{n}} of {{total}}",
    "progressLabel": "Quiz progress",
    "pickUpTo": "Pick up to {{n}}.",
    "optional": "Optional. Skip it if you're not sure.",
    "timeRange": "Between {{min}} and {{max}}.",
    "less": "One less",
    "more": "One more",
    "skip": "Skip",
    "finish": "See my plan"
  },
  "q": {
    "sleep_location": { "title": "Where does your baby sleep at night?", "crib_own_room": "Crib in their own room", "crib_parents_room": "Crib or bassinet in our room", "bed_sharing": "In our bed", "varies": "It varies" },
    "fall_asleep": { "title": "How does your baby usually fall asleep?", "feeding": "While feeding", "rocking": "Rocking or bouncing", "holding": "Held or on me", "pacifier": "With a pacifier", "motion": "In the stroller or car", "patting": "With me next to them", "independent": "On their own in the crib" },
    "night_wakings": { "title": "How often does your baby wake at night?", "none": "Sleeps through", "one": "Once", "two_three": "2–3 times", "four_plus": "4 or more times" },
    "wake_time": { "title": "When does your baby usually wake up for the day?" },
    "bedtime": { "title": "When does your baby usually fall asleep for the night?" },
    "naps_per_day": { "title": "How many naps a day?" },
    "struggles": { "title": "What's hardest about sleep right now?", "falling_asleep": "Falling asleep", "night_wakings": "Waking a lot at night", "early_waking": "Waking too early", "short_naps": "Short naps", "nap_resistance": "Fighting naps", "bedtime_battles": "Bedtime battles", "day_night_confusion": "Mixing up day and night", "irregular_schedule": "No predictable rhythm", "none": "Nothing, it's going well" },
    "feeding": { "title": "How is your baby fed?", "breast": "Breastfeeding", "bottle": "Bottle (formula or pumped milk)", "mixed": "Breast and bottle", "solids_and_milk": "Solids and milk", "mostly_solids": "Mostly table food" },
    "night_help": { "title": "Who helps at night?", "partner": "My partner", "family": "Family", "nanny": "A night nanny or caregiver", "no_one": "I do it alone" },
    "goals": { "title": "What would you like to change first?", "longer_night_sleep": "Longer stretches at night", "fewer_night_feeds": "Fewer night feeds", "independent_sleep": "Falling asleep more easily alone", "predictable_schedule": "A predictable day", "better_naps": "Better naps", "earlier_bedtime": "An earlier bedtime", "more_rest_for_me": "More rest for me", "understand_baby": "Understanding my baby's sleep" },
    "parent_energy": { "title": "How are you feeling these days?", "good": "Rested and fine", "okay": "Tired but okay", "tired": "Very tired", "exhausted": "Running on empty" }
  },
  "plan": {
    "missing": "Your plan isn't ready yet. Let's finish the questions.",
    "kicker": "Your plan",
    "title": "{{name}}'s sleep plan",
    "dayTitle": "Here is {{name}}'s day",
    "napsBedAt_one": "1 nap · bed at",
    "napsBedAt_other": "{{count}} naps · bed at",
    "dialLead": "Built from age and your answers. It gets sharper as you log sleep.",
    "looksRight": "Looks right",
    "flexible": "Newborns don't need a clock yet. Follow sleepy cues and feed on demand; we'll suggest times from about 8 weeks.",
    "wake": "Wake up",
    "bedtime": "Bedtime",
    "corrected": "Based on corrected age.",
    "shift": "Move bedtime from {{from}} to {{to}} slowly: {{step}} minutes earlier every {{days}} days.",
    "today": "A day like today",
    "wakeUp": "Wake up",
    "nap": "Nap {{n}}",
    "about": "about {{min}} min",
    "bedtimeItem": "Bedtime",
    "todayHint": "The plan adjusts as you log sleep.",
    "focus": "What we'll work on",
    "done": "Done"
  },
  "focus": {
    "night_wakings": { "title": "Fewer night wakings", "body": "Small changes to the evening can make nights longer." },
    "independent_sleep": { "title": "Falling asleep more easily", "body": "Step by step, less help at bedtime." },
    "night_feeds": { "title": "Fewer night feeds", "body": "A gentle way to move milk to the daytime." },
    "early_bedtime": { "title": "An earlier bedtime", "body": "Shifting bedtime a little at a time." },
    "early_waking": { "title": "Later mornings", "body": "What helps when the day starts too early." },
    "naps": { "title": "Better naps", "body": "Timing naps by wake windows." },
    "nap_transition": { "title": "Nap changes", "body": "How to know when to drop a nap." },
    "routine": { "title": "A calm routine", "body": "The same short steps every evening." },
    "day_night": { "title": "Day and night", "body": "Light, noise and feeds that teach the difference." },
    "sleepy_cues": { "title": "Sleepy cues", "body": "Catch the moment before overtired." },
    "parent_rest": { "title": "Rest for you", "body": "Ways to get more sleep yourself." },
    "shared_nights": { "title": "Sharing the nights", "body": "Taking turns so everyone sleeps." },
    "sleep_basics": { "title": "How baby sleep works", "body": "The basics in a few short lessons." }
  },
  "note": {
    "generic": "Please read our safe sleep guidance.",
    "safety_safe_sleep": "Safe sleep: the AAP recommends a separate, flat sleep surface in your room, baby on their back, with nothing else in the crib.",
    "night_short_for_age": "Nights look a bit short for this age. An earlier bedtime often helps.",
    "naps_outside_age_range": "{{reported}} naps is unusual at this age (usually {{min}}–{{max}}). We planned with the typical number."
  },
  "install": {
    "title": "Add Baby Plan to your Home Screen",
    "why": "Reminders only work from the Home Screen app. It also opens faster and works offline.",
    "ios1": "Tap Share in Safari's toolbar",
    "ios2": "Scroll down and tap Add to Home Screen",
    "ios3": "Tap Add, then open Baby Plan from your Home Screen",
    "iosDone": "I've added it",
    "android1": "Open the browser menu",
    "android2": "Tap Install app or Add to Home screen",
    "desktop1": "Open the browser menu or the install icon in the address bar",
    "desktop2": "Choose Install Baby Plan",
    "open3": "Open Baby Plan from your Home Screen",
    "nativeLead": "One tap installs it. No app store needed.",
    "button": "Add to Home Screen",
    "notNow": "Not now",
    "openFromHome": "Now open Baby Plan from your Home Screen. You'll turn on reminders there.",
    "appName": "Baby Plan",
    "continueHere": "Continue here for now"
  },
  "notify": {
    "title": "Reminders",
    "titleNaps": "A nudge before naps?",
    "titleBedtime": "A nudge before bedtime?",
    "titleOn": "Reminders are on",
    "onlyPlan": "Only plan reminders. Change them any time.",
    "previewLabel": "Example reminder",
    "previewNow": "now",
    "previewNap": "{{name}}: nap in {{min}} min",
    "previewBed": "{{name}}: bedtime in {{min}} min",
    "previewBody": "Planned around {{time}}. Start winding down.",
    "checking": "Checking this phone…",
    "summary": "We'll remind you {{min}} minutes before {{what}}.",
    "whatBoth": "naps and bedtime",
    "whatNaps": "naps",
    "whatBedtime": "bedtime",
    "summaryNewborn": "We'll remind you before bedtime once your baby has a rhythm. You can change this any time.",
    "needsInstall": "On iPhone, reminders only reach you from the Home Screen app. Open Baby Plan from your Home Screen and turn them on there.",
    "unsupported": "This browser can't show reminders. Try Safari on iPhone or Chrome on Android.",
    "denied": "Notifications are blocked for Baby Plan. You can allow them in your phone's settings, then turn reminders on in Settings.",
    "turnOn": "Remind me",
    "notNow": "Not now",
    "on": "You'll get them on this phone.",
    "testLead": "Want to check? Send a test reminder. Lock your phone to see how it arrives.",
    "testNow": "Send now",
    "testLater": "In 1 minute",
    "testSent": "Sent. It should arrive in a few seconds.",
    "testSentLater": "On its way in about a minute. Lock your phone now.",
    "toPlan": "Go to my plan",
    "errors": {
      "denied": "Notifications are blocked. You can allow them in your phone's settings.",
      "dismissed": "No problem. You can turn reminders on later in Settings.",
      "needs_install": "Open Baby Plan from your Home Screen first.",
      "unsupported": "This browser can't show reminders.",
      "push_unavailable": "Reminders aren't available right now. Try again later.",
      "no_worker": "The app is still setting up. Reload and try again.",
      "subscribe_failed": "This phone didn't accept reminders. Try again.",
      "offline": "You're offline. Connect and try again."
    }
  },
  "errors": {
    "generic": "Something went wrong. Please try again.",
    "offline": "You're offline. Connect to the internet and try again.",
    "birth_date_in_future": "The birth date can't be in the future.",
    "birth_date_too_old": "Baby Plan is made for children up to 3. Check the birth date.",
    "due_date_out_of_range": "That due date doesn't fit the birth date. Check both.",
    "too_many_babies": "You've added the most babies we support.",
    "validation_error": "Check the fields and try again.",
    "invalid_answer": "That answer didn't save. Try another one.",
    "missing": "One more answer is needed first.",
    "consent_required": "Please agree to the health-data terms first."
  }
}
